import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  Divider,
  Center,
  Alert,
  AlertIcon,
  useColorModeValue,
  CloseButton,
  Image,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Grid,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { ArrowDownIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { Certificate, CertificateRepository } from "api/CertificateRepository";
import Card from "components/card/Card";


export default function CertificateDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role") || "";
  const textColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.300", "whiteAlpha.100");
  const bgColor = useColorModeValue("blue.50", "navy.800");
  const subTextColor = useColorModeValue("gray.700", "gray.200");
  const badgeColorScheme = useColorModeValue("red", "green");
  const [headerContext, setHeaderContext] = useState<any>(null);
  const [certificate, setCertificate] = useState<Certificate | null>(null)
  const [pdfPreviews, setPdfPreviews] = useState<{ [key: string]: string }>({})
  const [csvPreviews, setCsvPreviews] = useState<{ [key: string]: string[][] }>({})
  const [imagePreviews, setImagePreviews] = useState<{ [key: string]: string }>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<"info" | "warning" | "success" | "error">("success");
  const ReuiredType = [
    "Callibration",
    "Tracability",
    "Invoice",
    "ServiceReport",
    "Others",
  ];

  const uploadedTypes =
    certificate?.certificates?.map((cert: any) =>
      cert.type?.trim()

    ) || [];

  const pendingCertificates = ReuiredType.filter(
    (type) => !uploadedTypes.includes(type)
  );

  useEffect(() => {
    const id = location.state?.id || null;
    const certificateId = location.state?.certificateId || null;
    const calibrationId = location.state?.calibrationId;

    if (calibrationId)
      CertificateRepository.getCertificatesByCalibrationId(calibrationId!)
        .then(setCertificate);
    if (id || certificateId) {
      CertificateRepository.getCertificateById(id || certificateId)
        .then((data) => {
          setCertificate(data);

          CertificateRepository.getHeaderContext(
            data.orgId,
            data.locationId,
            data.assetId,
            data.vendorId,
            data.departmentId

          )
            .then((headerData) => {
              setHeaderContext(headerData);
            })
            .catch((err) => {

            });
        })
        .catch((error) => {
        });
    }

  }, [location.state])

  // Show center alert
  const showCenterAlert = (message: string, status: "success" | "error") => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 3000);
  };

  useEffect(() => {
    if (!certificate?.certificates) return;

    certificate.certificates.forEach((cert: any) => {
      const lower = cert.fileName.toLowerCase();

      if (lower.endsWith(".pdf")) {
        loadPdfPreview(cert.fileUrl, cert.fileName);
      } else if (
        lower.endsWith(".jpg") ||
        lower.endsWith(".jpeg") ||
        lower.endsWith(".png")
      ) {
        loadImagePreview(cert.fileUrl, cert.fileName);
      } else if (lower.endsWith(".csv")) {
        loadCsvPreview(cert.fileUrl, cert.fileName);
      }
    });
  }, [certificate]);
  // Handle download
  const handleDownload = async (certificateId: string, fileName: string) => {
    try {
      const blob = await CertificateRepository.downloadCertificate(certificateId);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "certificate";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showCenterAlert(`File "${fileName}" downloaded successfully!`, "success");
    } catch (err) {
      showCenterAlert("Download failed. Please try again.", "error");
    }
  };

  // Fetch PDF for preview
  const loadPdfPreview = async (certificateId: string, fileName: string) => {
    if (pdfPreviews[fileName]) return;

    try {
      const blob = await CertificateRepository.downloadCertificate(certificateId);
      const blobUrl = URL.createObjectURL(blob);
      setPdfPreviews((prev) => ({ ...prev, [fileName]: blobUrl }));
    } catch (err) {

      showCenterAlert("PDF preview failed. Please try again.", "error");
    }
  };

  // Fetch CSV for preview
  const loadCsvPreview = async (certificateId: string, fileName: string) => {
    if (csvPreviews[fileName]) return;

    try {
      const blob = await CertificateRepository.downloadCertificate(certificateId);
      const text = await blob.text();
      const rows = text.split("\n").map((row) => row.split(","));
      const previewRows = rows.slice(0, 5);
      setCsvPreviews((prev) => ({ ...prev, [fileName]: previewRows }));
    } catch (err) {
      showCenterAlert("CSV preview failed. Please try again.", "error");
    }
  };

  // fetch jpg png preview
  const loadImagePreview = async (fileUrl: string, fileName: string) => {
    if (imagePreviews[fileName]) return;

    try {
      setLoading((prev) => ({ ...prev, [fileName]: true }));
      const blob = await CertificateRepository.downloadCertificate(fileUrl);
      const blobUrl = URL.createObjectURL(blob);
      setImagePreviews((prev) => ({ ...prev, [fileName]: blobUrl }));
    } catch (err) {
      showCenterAlert("Image preview failed. Please try again.", "error");
    } finally {
      setLoading((prev) => ({ ...prev, [fileName]: false }));
    }
  };
  // Render preview
  const renderFilePreview = (fileUrl: string, fileName: string) => {
    const lowerName = fileName.toLowerCase();

    if (lowerName.endsWith(".pdf")) {
      const previewUrl = pdfPreviews[fileName];

      return (
        <Box mt={2} w="100%" h="300px" border="1px solid" borderColor={borderColor} borderRadius="8px">
          {previewUrl ? (
            <iframe src={previewUrl} width="100%" height="100%" />
          ) : (
            <Center h="100%">
              <Spinner />
            </Center>
          )}
        </Box>
      );
    }

    if (
      lowerName.endsWith(".jpg") ||
      lowerName.endsWith(".jpeg") ||
      lowerName.endsWith(".png")
    ) {
      const previewUrl = imagePreviews[fileName];
      const isLoading = loading[fileName];

      return (
        <Box mt={2} border="1px solid" borderColor={borderColor} borderRadius="8px" p={2}>
          {isLoading ? (
            <Center><Spinner /></Center>
          ) : previewUrl ? (
            <Image src={previewUrl} maxH="300px" mx="auto" />
          ) : null}
        </Box>
      );
    }

    if (lowerName.endsWith(".csv")) {
      const previewRows = csvPreviews[fileName];

      if (!previewRows) return <Spinner size="sm" mt={2} />;

      return (
        <Table size="sm" mt={2} variant="striped">
          <Thead>
            <Tr>
              {previewRows[0].map((cell, i) => (
                <Th key={i}>{cell}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {previewRows.slice(1).map((row, i) => (
              <Tr key={i}>
                {row.map((cell, j) => (
                  <Td key={j}>{cell}</Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      );
    }

    return (
      <Text mt={2} fontSize="sm" color="gray.500">
        Preview not available
      </Text>
    );
  };

  return (
    <Card mt={{ base: "20px", md: "50px" }} p={6}>
      {/* Center Alert */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      <Box borderWidth="1px" borderRadius="lg" p={6} bg={bgColor} borderColor={borderColor}>
        <Flex mb={4} justify="space-between" align="center">
          <CloseButton
            position="absolute"
            top="40px"
            right="60px"
            size="lg"
            onClick={() => navigate(role === "Admin" ? "/admin/CertificateList" : "/admin/asset-management/assetList", { state: location.state })}
          />
        </Flex>

        {(role !== "Vendor" && role !== "DeptUser" && role !== "OrgAdmin" && role !== "LocationAdmin") && (
          <>
            <Flex justify="space-between">
              <Text fontWeight="bold" color={subTextColor}>
                Calibration Id :{" "}
                <Box as="span" fontWeight="extrabold">
                  {certificate?.calibrationId}
                  <Text mt={1}>
                    <strong>Certificate Uploaded: </strong>{" "}
                    {new Date(certificate?.uploadedAt || "").toLocaleString()}
                  </Text>
                  <Text fontWeight="bold" color={subTextColor}>
                    Pending Certificate :{" "}
                    <Box as="span" color="red.500">
                      {pendingCertificates.length > 0
                        ? pendingCertificates.join(", ")
                        : "None"}
                    </Box>
                  </Text>

                </Box>
              </Text>

              <Badge colorScheme={badgeColorScheme} mt={10}>
                {certificate?.status}
              </Badge>
            </Flex>

            <Box
              w={{ sm: "330px", md: "700px", lg: "900px" }}
              mt={{ md: "30px" }}
              zIndex={2}
            >
              <Grid templateColumns="repeat(4, 1fr)" gap="20px">
                <VStack
                  spacing={2}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg={textColor}
                  shadow="md"
                  align="start"
                >
                  <Text fontWeight="bold" textAlign="center" w="100%">
                    Asset
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">
                    Name: {headerContext?.assetInfo?.name || "_"}
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">
                    Type : {headerContext?.assetInfo?.type || "_"}
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">
                    Status : {headerContext?.assetInfo?.status || "_"}
                  </Text>
                </VStack>

                <VStack
                  spacing={2}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg={textColor}
                  shadow="md"
                  align="start"
                >
                  <Text fontWeight="bold" textAlign="center" w="100%">
                    Vendor
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">
                    Name : {headerContext?.vendorInfo?.name || "_"}
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">
                    Contact : {headerContext?.vendorInfo?.contactNumber || "_"}
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">
                    GsTin : {headerContext?.vendorInfo?.gsTin || "_"}
                  </Text>
                </VStack>
              </Grid>
            </Box>
          </>
        )}

        <Divider my={4} borderColor={borderColor} p="10px" />

        <Box color={subTextColor}>
          {!certificate?.certificates || certificate.certificates.length === 0 ? (
            <Center py={10}>
              <VStack spacing={3}>
                <Text fontSize="lg" fontWeight="bold" color="red.500">
                  Certificate Not Found
                </Text>
                <Text fontSize="sm" color="gray.500">
                  No certificate files are available for this record.
                </Text>
              </VStack>
            </Center>
          ) : (
            certificate.certificates.map(
              (cert: { fileUrl: string; fileName: string }, index: number) => (
                <Box
                  key={index}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  mt={3}
                  bg="whiteAlpha.100"
                >
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="600">{cert.fileName}</Text>
                    <Button
                      colorScheme="blue"
                      size="sm"
                      onClick={() => handleDownload(cert.fileUrl, cert.fileName)}
                    >
                      Download <ArrowDownIcon ml={1} />
                    </Button>
                  </Flex>

                  {renderFilePreview(cert.fileUrl, cert.fileName)}
                </Box>
              )
            )
          )}
        </Box>
      </Box >

    </Card >
  );
}